import { Box, Typography, useTheme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart as BarChartIcon, DonutLarge as DonutLargeIcon } from '@mui/icons-material';
import { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import { tokens } from '../theme';
import { useTranslation } from '../hooks/useTranslation';

const SimulationsChart = ({ proposalStats }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [chartType, setChartType] = useState('bar');

  const handleChartChange = (event, newChartType) => {
    if (newChartType !== null) {
      setChartType(newChartType);
    }
  };

  const chartData = [
    {
      name: t('dashboard.finished'),
      value: proposalStats.finished?.length || 0,
      color: colors.grey[500],
      percentage: 0,
    },
    {
      name: t('dashboard.pendent'),
      value: proposalStats.open?.length || 0,
      color: '#ff9800',
      percentage: 0,
    },
    {
      name: t('dashboard.rejected'),
      value: proposalStats.rejected?.length || 0,
      color: '#9c27b0',
      percentage: 0,
    },
    {
      name: t('dashboard.approved'),
      value: proposalStats.approved?.length || 0,
      color: colors.greenAccent[500],
      percentage: 0,
    },
  ];

  // Calcular percentuais
  const total = chartData.reduce((sum, item) => sum + item.value, 0);
  chartData.forEach((item) => {
    item.percentage = total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
  });

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos((-midAngle * Math.PI) / 180);
    const y = cy + radius * Math.sin((-midAngle * Math.PI) / 180);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="14"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CustomLegend = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        ml: { xs: 0, md: 24 },
        width: { xs: '100%', md: 'auto' },
        alignItems: 'flex-start',
      }}
    >
      {chartData.map((entry, index) => (
        <Box key={index}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              mb: '6px',
            }}
          >
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                backgroundColor: entry.color,
              }}
            />

            <Typography color={colors.grey[400]} sx={{ letterSpacing: '1px' }}>
              {entry.name}
            </Typography>
          </Box>

          <Typography fontSize="18px" color={colors.grey[100]} sx={{ ml: { xs: 0, md: '24px' } }}>
            % {entry.percentage}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #272C3580',
        p: '20px',
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb="20px"
        flexShrink={0}
      >
        <Typography variant="h5" fontWeight="600" color={colors.grey[100]}>
          {t('dashboard.simulations')}
        </Typography>

        {/* Toggle Chart Type */}
        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              color: colors.grey[600],
              borderColor: colors.grey[600],
              borderRadius: '4px',
              '&.Mui-selected': {
                backgroundColor: '#1CCA5B1A',
                borderColor: colors.greenAccent[500],
                color: colors.greenAccent[500],
                '&:hover': {
                  backgroundColor: colors.greenAccent[600],
                },
              },
            },
            display: 'flex',
            gap: '12px',
          }}
        >
          <ToggleButton value="bar" aria-label="bar chart">
            <BarChartIcon />
          </ToggleButton>
          <ToggleButton value="donut" aria-label="donut chart">
            <DonutLargeIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Chart Area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            width: '100%',
            height: '100%',
            gap: '24px',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {chartType === 'bar' ? (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={chartData}
                  margin={{
                    top: 5,
                    right: 5,
                    left: -25,
                    bottom: 5,
                  }}
                >
                  <XAxis
                    dataKey="name"
                    tick={{ fill: colors.grey[100] }}
                    axisLine={{ stroke: colors.grey[700] }}
                  />
                  <YAxis
                    tick={{ fill: colors.grey[100] }}
                    axisLine={{ stroke: colors.grey[700] }}
                  />
                  <Bar dataKey="value" radius={[2.8, 2.8, 0, 0]} barSize={10}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <CustomLegend />
            </>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={100}
                    innerRadius={60}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <CustomLegend />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SimulationsChart;
